import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  MoveToInbox,
  Assignment,
  People,
  Person,
  Receipt,
  Assessment,
  Settings,
  Logout,
  PointOfSale,
  Fullscreen,
  FullscreenExit,
  Lock as LockIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { APP_NAME } from '../constants/app';
import packageJson from '../../package.json';
import logo from '../assets/tienda-logo-alpha.png';
import { usersAPI, apiUtils } from '../services/api';
import UserAvatar from './UserAvatar';
const APP_VERSION = packageJson.version;

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'minimized' })(
  ({ theme, open, minimized }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    height: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && !minimized && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
    ...(!open && minimized && {
      marginLeft: '0px',
    }),
    ...(open && minimized && {
      marginLeft: '60px',
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }),
);

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'minimized',
})(({ theme, open, minimized }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && !minimized && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(minimized && {
    width: `calc(100% - 60px)`,
    marginLeft: '60px',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const Layout = ({ children, user, onLogout, currentPage, onNavigate }) => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerMinimized, setDrawerMinimized] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasOngoingTransaction, setHasOngoingTransaction] = useState(true); // hardcoded for demonstration
  const [confirmLeaveDialog, setConfirmLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Password change dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success', 'error', 'warning', 'info'
  });

  const handleDrawerToggle = () => {
    if (drawerOpen && !drawerMinimized) {
      setDrawerMinimized(true);
    } else if (drawerOpen && drawerMinimized) {
      setDrawerOpen(false);
      setDrawerMinimized(false);
    } else {
      setDrawerOpen(true);
    }
  };

  const handleDrawerMinimizeToggle = () => {
    setDrawerMinimized((prev) => !prev);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    onLogout();
  };

  // Helper function to show notifications
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleChangePassword = () => {
    setPasswordDialogOpen(true);
    handleProfileMenuClose();
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setPasswordError('');
  };

  const handlePasswordInputChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setPasswordError(''); // Clear error when user types
  };

  const handlePasswordSubmit = async () => {
    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      setPasswordError('Please fill in all fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);
    try {
      await usersAPI.updatePassword(user.id, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      handlePasswordDialogClose();
      showNotification('Password changed successfully!', 'success');
    } catch (error) {
      const errorMessage = apiUtils.handleError(error);
      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement != null);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  const handleConfirmLeave = () => {
    setConfirmLeaveDialog(false);
    if (pendingNavigation) {
      onNavigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelLeave = () => {
    setConfirmLeaveDialog(false);
    setPendingNavigation(null);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, page: 'Dashboard' },
    { text: 'POS', icon: <PointOfSale />, page: 'POS' },
    { text: 'Transactions', icon: <Receipt />, page: 'Transactions' },
    { text: 'Products', icon: <Inventory />, page: 'Products' },
    { text: 'Product Movements', icon: <MoveToInbox />, page: 'Product Movements' },
    { text: 'Inventory Closing', icon: <Assignment />, page: 'Inventory Closing' },
    { text: 'Customers', icon: <Person />, page: 'Customers' },
    { text: 'Users', icon: <People />, page: 'Users' },
    { text: 'Reports', icon: <Assessment />, page: 'Reports' },
    { text: 'Settings', icon: <Settings />, page: 'Settings' },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBarStyled position="fixed" open={drawerOpen && !drawerMinimized} minimized={drawerMinimized}>
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <img 
              src={logo} 
              alt="Tienda One Logo" 
              style={{ height: '32px', width: 'auto' }}
            />
            {/* {APP_NAME} | {currentPage} */}
            { JSON.parse(localStorage.getItem('user'))?.entity_name || APP_NAME } | {currentPage}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <UserAvatar 
              seed={user?.email || user?.username || 'default'} 
              size={40}
              style="thumbs"
              sx={{ 
                border: '2px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                }
              }}
            />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <Typography textAlign="center">{user?.username}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleChangePassword}>
              <LockIcon sx={{ mr: 1 }} /> Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
            <Divider />
            <MenuItem disabled sx={{ justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">v{APP_VERSION}</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBarStyled>
      
      <Drawer
        sx={{
          width: drawerMinimized ? 64 : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerMinimized ? 64 : drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
            overflowX: 'hidden',
            transition: 'width 0.3s',
          },
          display: 'flex',
          flexDirection: 'column',
        }}
        variant="persistent"
        anchor="left"
        open={drawerOpen}
      >
        <DrawerHeader>
          {drawerMinimized ? (
            <IconButton onClick={handleDrawerMinimizeToggle} size="small">
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              <Typography variant="h6" sx={{ flexGrow: 1, pl: 2 }}>
                Menu
              </Typography>
              <IconButton onClick={handleDrawerMinimizeToggle} size="small" sx={{ ml: 1 }}>
                <MenuIcon />
              </IconButton>
            </>
          )}
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.text}
              onClick={() => {
                
                if (currentPage === 'POS' && item.page !== 'POS' && hasOngoingTransaction) {
                  setPendingNavigation(item.page);
                  setConfirmLeaveDialog(true);
                  return;
                }
                onNavigate(item.page);
              }}
              selected={currentPage === item.page}
              sx={{
                justifyContent: 'center',
                px: drawerMinimized ? 0 : 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.dark',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <React.Fragment>
                  <Tooltip title={item.text} enterDelay={300} placement="right" disableHoverListener={!drawerMinimized}>
                    <ListItemIcon sx={{ color: currentPage === item.page ? 'primary.main' : 'inherit', minWidth: 0, justifyContent: 'center' }}>
                      {item.icon}
                    </ListItemIcon>
                  </Tooltip>
                  {!drawerMinimized && <ListItemText sx={{ ml: 1 }} primary={item.text} />}
                </React.Fragment>
              </Box>
            </ListItem>
          ))}
        </List>
        <Box sx={{ mt: 'auto', mb: 2, display: 'flex', justifyContent: 'center' }}>
          <IconButton onClick={toggleFullscreen} color="inherit">
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Box>
      </Drawer>
      
  <Main open={drawerOpen && !drawerMinimized} minimized={drawerMinimized}>
        <DrawerHeader />
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {children}
        </Box>
      </Main>

      {/* Confirm Leave POS Dialog */}
      <Dialog open={confirmLeaveDialog} onClose={handleCancelLeave}>
        <DialogTitle>Confirm Navigation</DialogTitle>
        <DialogContent>
          <Typography>
            You have an ongoing transaction. Are you sure you want to leave the POS module?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave}>Cancel</Button>
          <Button onClick={handleConfirmLeave} color="error" variant="contained">
            Leave POS
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handlePasswordDialogClose}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          
          <TextField
            name="current_password"
            label="Current Password"
            type="password"
            value={passwordData.current_password}
            onChange={handlePasswordInputChange}
            fullWidth
            required
            margin="normal"
            disabled={passwordLoading}
          />
          
          <TextField
            name="new_password"
            label="New Password"
            type="password"
            value={passwordData.new_password}
            onChange={handlePasswordInputChange}
            fullWidth
            required
            margin="normal"
            disabled={passwordLoading}
            helperText="Password must be at least 6 characters long"
          />
          
          <TextField
            name="confirm_password"
            label="Confirm New Password"
            type="password"
            value={passwordData.confirm_password}
            onChange={handlePasswordInputChange}
            fullWidth
            required
            margin="normal"
            disabled={passwordLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose} disabled={passwordLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordSubmit} 
            variant="contained" 
            disabled={passwordLoading}
          >
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;
